import React, { useState, useRef, useEffect, useMemo } from 'react';
import './dealer-search-dropdown.scss';
import useMarketDealerInfoService from '../../../../../hooks/owners-osb/use-market-dealerInfo-service';
import { useOSBStep } from '../../../../../hooks/owners-osb';
import GoogleMapService from '../../../../../services/osb-service/google-map-service/google-map-service';
import { FMInput } from '@own/fds-react';

// Types for options
interface DealerOption {
    label: string;
    type: 'dealer';
    dealerCode: string;
}

interface LocationOption {
    label: string;
    type: 'location';
}

interface PostcodeOption {
    label: string;
    type: 'postcode';
}

type CombinedOption = DealerOption | LocationOption | PostcodeOption;

const normalizeInputValue = (value: string): string =>
    value.trim().toLowerCase();

interface Props {
    onInputChange?: (value: string) => void;
    onSelect: (value: string, type: 'dealer' | 'location' | 'postcode') => void;
    placeholder?: string;
    showFilter?: boolean;
    locationLabel?: string;
    serviceCentreLabel?: string;
    selectedValue?: string;
    hasPostcodeSearch?: boolean;
    dataTestId?: string;
}

const DealerSearchDropdown = ({
    onSelect,
    onInputChange,
    placeholder = '',
    showFilter = true,
    locationLabel = '',
    serviceCentreLabel = '',
    selectedValue = '',
    hasPostcodeSearch = false,
    dataTestId = '',
}: Props) => {
    const { fetchMarketDealerInfo } = useMarketDealerInfoService();
    const { osbStep } = useOSBStep();
    const [inputValue, setInputValue] = useState(selectedValue ?? '');
    const [filteredDealerOptions, setFilteredDealerOptions] = useState<
        { dealerCode: string; dealerName: string }[]
    >([]);
    const [filteredLocationOptions, setFilteredLocationOptions] = useState<
        string[]
    >([]);
    const [filteredPostcodeOptions, setFilteredPostcodeOptions] = useState<
        string[]
    >([]);
    const [showOptions, setShowOptions] = useState(false);
    const [selectedOptionLabel, setSelectedOptionLabel] = useState(
        selectedValue ?? ''
    );
    const [dealers, setDealers] = useState<
        { dealerCode: string; dealerName: string }[]
    >([]);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [hoveredIndex, setHoveredIndex] = useState(-1);
    const inputRef = useRef<HTMLElement | null>(null);
    const optionsListRef = useRef<HTMLUListElement>(null);
    const inputValueRef = useRef<string>(inputValue);
    const selectedOptionLabelRef = useRef<string>('');
    const deepLinkParams = new URLSearchParams(
        typeof window !== 'undefined' ? window.location.search : ''
    );
    const [showLocations, setShowLocations] = useState(true);
    const [showServiceCentres, setShowServiceCentres] = useState(true);

    useEffect(() => {
        GoogleMapService.loadScript('google');
        const fetchDealers = async () => {
            const data = await fetchMarketDealerInfo(deepLinkParams);
            const filteredDealers = data?.dealers.filter(
                (dealer: { dealerCode: string }) =>
                    dealer.dealerCode !== '00001'
            );
            filteredDealers && setDealers(filteredDealers);
        };
        fetchDealers();
    }, []);

    useEffect(() => {
        setInputValue(selectedValue ?? '');
        setSelectedOptionLabel(selectedValue ?? '');
    }, [selectedValue]);

    useEffect(() => {
        inputValueRef.current = inputValue;
    }, [inputValue]);

    useEffect(() => {
        selectedOptionLabelRef.current = selectedOptionLabel;
    }, [selectedOptionLabel]);

    const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        if (
            selectedOptionLabel &&
            normalizeInputValue(value) !==
                normalizeInputValue(selectedOptionLabel)
        ) {
            setSelectedOptionLabel('');
        }

        setInputValue(value);
        if (onInputChange) {
            onInputChange(value);
        }

        if (!value.trim()) {
            setFilteredDealerOptions([]);
            setFilteredLocationOptions([]);
            setFilteredPostcodeOptions([]);
            setShowOptions(false);
            return;
        }

        updateFilteredOptions(value);
        setShowOptions(true);

        if (hasPostcodeSearch) {
            GoogleMapService.getPlacePredictions(
                value,
                updatePostcodeSuggestions,
                osbStep.geoCountryCode,
                true
            );
        } else {
            GoogleMapService.getPlacePredictions(
                value,
                updateLocationSuggestions,
                osbStep.geoCountryCode,
                false
            );
        }
    };

    const handleSelect = (option: CombinedOption) => {
        selectedOptionLabelRef.current = option.label;
        inputValueRef.current = option.label;
        setSelectedOptionLabel(option.label);
        setInputValue(option.label);
        onSelect(
            option.type === 'dealer' ? option.dealerCode : option.label,
            option.type
        );
        setFilteredDealerOptions([]);
        setFilteredLocationOptions([]);
        setFilteredPostcodeOptions([]);
        setShowOptions(false);
    };

    const updateFilteredOptions = (value: string) => {
        const escapedValue = escapeString(value);
        const dealerMatches = dealers
            .filter(dealer =>
                dealer.dealerName
                    .toLowerCase()
                    .includes(escapedValue.toLowerCase())
            )
            .map(dealer => ({
                dealerCode: dealer.dealerCode,
                dealerName: dealer.dealerName,
            }));
        setFilteredDealerOptions(dealerMatches);
    };

    useEffect(() => {
        const selectedValueLocked =
            selectedOptionLabel.length > 0 &&
            normalizeInputValue(inputValue) ===
                normalizeInputValue(selectedOptionLabel);

        if (selectedValueLocked) {
            setFilteredDealerOptions([]);
            return;
        }

        if (!inputValue.trim()) {
            setFilteredDealerOptions([]);
            return;
        }

        updateFilteredOptions(inputValue);
    }, [dealers, inputValue, selectedOptionLabel]);

    const updateLocationSuggestions = (predictions: any) => {
        const selectedValueLocked =
            selectedOptionLabelRef.current.length > 0 &&
            normalizeInputValue(inputValueRef.current) ===
                normalizeInputValue(selectedOptionLabelRef.current);

        if (selectedValueLocked) {
            setFilteredLocationOptions([]);
            return;
        }

        const locationMatches = (predictions || []).map(
            (prediction: any) => prediction.description
        );
        setFilteredLocationOptions(locationMatches);
    };

    const updatePostcodeSuggestions = (predictions: any) => {
        const selectedValueLocked =
            selectedOptionLabelRef.current.length > 0 &&
            normalizeInputValue(inputValueRef.current) ===
                normalizeInputValue(selectedOptionLabelRef.current);

        if (selectedValueLocked) {
            setFilteredPostcodeOptions([]);
            return;
        }

        const postcodeMatches = (predictions || []).map(
            (prediction: any) => prediction.structured_formatting.main_text
        );
        setFilteredPostcodeOptions(postcodeMatches);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (
            inputRef.current &&
            !inputRef.current.contains(event.target as Node)
        ) {
            setShowOptions(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!showOptions) return;

        if (event.key === 'ArrowDown') {
            setHighlightedIndex(prevIndex => {
                const newIndex =
                    prevIndex < visibleOptions.length - 1
                        ? prevIndex + 1
                        : prevIndex;
                scrollToItem(newIndex);
                return newIndex;
            });
        } else if (event.key === 'ArrowUp') {
            setHighlightedIndex(prevIndex => {
                const newIndex = prevIndex > 0 ? prevIndex - 1 : prevIndex;
                scrollToItem(newIndex);
                return newIndex;
            });
        } else if (event.key === 'Enter' && highlightedIndex >= 0) {
            handleSelect(visibleOptions[highlightedIndex]);
            setHighlightedIndex(-1);
        }
    };

    const scrollToItem = (index: number) => {
        if (optionsListRef.current) {
            const listItem = optionsListRef.current.children[
                index
            ] as HTMLElement;
            if (listItem) {
                listItem.scrollIntoView({ block: 'nearest' });
            }
        }
    };

    const handleLocationCheckboxChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setShowLocations(event.target.checked);
    };

    const handleServiceCentreCheckboxChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setShowServiceCentres(event.target.checked);
    };

    const escapeString = (unsafe: string): string => {
        return unsafe
            .replace(/\\/g, '\\\\')
            .replace(/\[/g, '\\[')
            .replace(/\]/g, '\\]')
            .replace(/\(/g, '\\(')
            .replace(/\)/g, '\\)');
    };

    // Determine which options to display based on `hasPostcodeSearch`
    const currentOptions: CombinedOption[] = hasPostcodeSearch
        ? filteredPostcodeOptions.map(option => ({
              label: option,
              type: 'postcode' as const,
          }))
        : [
              ...filteredDealerOptions
                  .filter(() => showServiceCentres)
                  .map(option => ({
                      label: option.dealerName,
                      type: 'dealer' as const,
                      dealerCode: option.dealerCode,
                  })),
              ...filteredLocationOptions
                  .filter(() => showLocations)
                  .map(option => ({
                      label: option,
                      type: 'location' as const,
                  })),
          ];

    const isSelectedValueLocked = useMemo(
        () =>
            selectedOptionLabel.length > 0 &&
            normalizeInputValue(inputValue) ===
                normalizeInputValue(selectedOptionLabel),
        [inputValue, selectedOptionLabel]
    );

    const visibleOptions = useMemo(() => {
        const normalizedSelectedOptionLabel = normalizeInputValue(
            selectedOptionLabel
        );
        return currentOptions.filter(
            option =>
                !normalizedSelectedOptionLabel ||
                normalizeInputValue(option.label) !==
                    normalizedSelectedOptionLabel
        );
    }, [currentOptions, selectedOptionLabel]);

    const shouldShowOptions =
        showOptions &&
        inputValue.trim().length > 0 &&
        !isSelectedValueLocked &&
        visibleOptions.length > 0;

    const listboxId = `${dataTestId || 'dealer-search'}-listbox`;
    const getOptionId = (index: number) => `${listboxId}-option-${index}`;
    const activeDescendantId =
        shouldShowOptions && highlightedIndex >= 0
            ? getOptionId(highlightedIndex)
            : undefined;
    const getOptionClassName = (
        option: CombinedOption,
        index: number,
        highlightedIndex: number,
        hoveredIndex: number
    ): string => {
        const classNames = [];
        if (option.type === 'dealer') {
            classNames.push('dealer-option');
        } else if (option.type === 'location') {
            classNames.push('location-option');
        } else {
            classNames.push('postcode-option');
        }

        if (index === highlightedIndex || index === hoveredIndex) {
            classNames.push('highlighted');
        }

        return classNames.join(' ');
    };
    return (
        <div
            className="osb-v3-dropdown-container"
            ref={node => {
                inputRef.current = node;
            }}
        >
            <FMInput
                dataTestId={dataTestId}
                type="text"
                value={inputValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                required={true}
                placeholder={placeholder}
                role="combobox"
                aria-autocomplete="list"
                aria-haspopup="listbox"
                aria-expanded={shouldShowOptions}
                aria-controls={listboxId}
                aria-activedescendant={activeDescendantId}
            />
            {shouldShowOptions && (
                <ul
                    className="options-list"
                    ref={optionsListRef}
                    id={listboxId}
                    role="listbox"
                >
                    {showFilter && (
                        <li className="checkbox-item" role="presentation">
                            <div className="checkbox-container">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={showLocations}
                                        onChange={handleLocationCheckboxChange}
                                    />
                                    {locationLabel}
                                </label>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={showServiceCentres}
                                        onChange={
                                            handleServiceCentreCheckboxChange
                                        }
                                    />
                                    {serviceCentreLabel}
                                </label>
                            </div>
                        </li>
                    )}
                    {visibleOptions.map((option, index) => (
                        <li
                            id={getOptionId(index)}
                            key={`${option.type}-${
                                option.type === 'dealer'
                                    ? option.dealerCode
                                    : option.label
                            }`}
                            role="option"
                            aria-selected={index === highlightedIndex}
                            onClick={() => handleSelect(option)}
                            onKeyDown={event => {
                                if (
                                    event.key === 'Enter' ||
                                    event.key === ' '
                                ) {
                                    handleSelect(option);
                                }
                            }}
                            onMouseEnter={() => setHoveredIndex(index)}
                            onMouseLeave={() => setHoveredIndex(-1)}
                            className={getOptionClassName(
                                option,
                                index,
                                highlightedIndex,
                                hoveredIndex
                            )}
                        >
                            {option.label}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default DealerSearchDropdown;
